import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from 'core/api/ConnectionManager'
import helpersFiles from 'core/helpers/_files'
import { CatalogTabId, CatalogFileItem, CanvasBgMedia } from '../templateEditor.types'
import { elementTypeFromFile } from '../elements/createElement'
import { PlaceModeOptions } from '../templateEditor.types'

type CatalogResponse = {
    data?: unknown[]
    nextPageToken?: string
}

type CatalogBreadcrumb = {
    id: number | null
    name: string
}

const parseResolution = (resolution?: string | null) => {
    if (!resolution) {
        return { width: undefined, height: undefined }
    }

    const [width, height] = resolution.split('x').map(Number)
    return {
        width: Number.isFinite(width) ? width : undefined,
        height: Number.isFinite(height) ? height : undefined,
    }
}

const mapCatalogItem = (raw: Record<string, unknown>): CatalogFileItem | null => {
    if (raw.isDirectory) {
        return {
            id: Number(raw.id),
            name: String(raw.name ?? ''),
            fileType: 'folder',
            isDirectory: true,
        }
    }

    const fileItem = helpersFiles.getFileItem(raw)
    const resolution = parseResolution(helpersFiles.getFileResolution(raw))

    return {
        id: Number(fileItem.id),
        name: String(fileItem.name ?? ''),
        fileType: String(fileItem.fileType ?? ''),
        isDirectory: false,
        thumbnail: fileItem.thumbnail,
        src: fileItem.src,
        width: resolution.width,
        height: resolution.height,
    }
}

const extractRows = (response: CatalogResponse | unknown[]): unknown[] => {
    if (Array.isArray(response)) {
        return response
    }

    return Array.isArray(response?.data) ? response.data : []
}

export const useTemplateEditorCatalog = (catalogTab: CatalogTabId) => {
    const [folderId, setFolderId] = useState<number | null>(null)
    const [rootFolderId, setRootFolderId] = useState<number | null>(null)
    const [rootFolderName, setRootFolderName] = useState('root')
    const [items, setItems] = useState<CatalogFileItem[]>([])
    const [loading, setLoading] = useState(false)
    const [catalogSearch, setCatalogSearch] = useState('')
    const [breadcrumbs, setBreadcrumbs] = useState<CatalogBreadcrumb[]>([{ id: null, name: 'root' }])
    const rootFolderIdRef = useRef<number | null>(null)
    const rootFolderNameRef = useRef('root')
    const requestTokenRef = useRef(0)

    rootFolderIdRef.current = rootFolderId
    rootFolderNameRef.current = rootFolderName

    const mediaItems = useMemo(() => {
        const query = catalogSearch.trim().toLowerCase()

        return items.filter((item) => {
            if (item.isDirectory) {
                if (query && !item.name.toLowerCase().includes(query)) {
                    return false
                }
                return true
            }

            if (catalogTab === 'all') {
                if (item.fileType !== 'image' && item.fileType !== 'video') {
                    return false
                }
            } else if (catalogTab === 'backgrounds') {
                if (item.fileType !== 'image' && item.fileType !== 'video') {
                    return false
                }
            } else if (item.fileType !== 'image') {
                return false
            }

            if (query && !item.name.toLowerCase().includes(query)) {
                return false
            }

            return true
        })
    }, [catalogSearch, catalogTab, items])

    const fetchFolderContents = useCallback(async (nextFolderId: number) => {
        const response = await api.send<Record<string, unknown>, CatalogResponse | unknown[]>(
            'getFilesAndFolders',
            {
                limit: 60,
                folderId: nextFolderId,
            },
            { hideLoader: true }
        )

        const rows = extractRows(response)
        return rows
            .map((row) => mapCatalogItem(row as Record<string, unknown>))
            .filter((item): item is CatalogFileItem => Boolean(item))
    }, [])

    const resolveRootFolderId = useCallback(async (): Promise<{ id: number; name: string } | null> => {
        if (rootFolderIdRef.current != null) {
            return { id: rootFolderIdRef.current, name: rootFolderNameRef.current }
        }

        const response = await api.send<object, CatalogResponse | unknown[]>(
            'getFilesAndFolders',
            { limit: 60 },
            { hideLoader: true }
        )

        const rows = extractRows(response)
            .map((row) => mapCatalogItem(row as Record<string, unknown>))
            .filter((item): item is CatalogFileItem => Boolean(item))

        if (rows.length === 1 && rows[0].isDirectory) {
            const root = rows[0]
            const name = root.name || 'root'
            setRootFolderId(root.id)
            setRootFolderName(name)
            rootFolderIdRef.current = root.id
            rootFolderNameRef.current = name
            return { id: root.id, name }
        }

        return null
    }, [])

    const loadFolder = useCallback(
        async (nextFolderId: number | null, options?: { resetBreadcrumbs?: boolean }) => {
            const requestToken = ++requestTokenRef.current
            setLoading(true)

            try {
                let targetFolderId = nextFolderId

                if (targetFolderId == null) {
                    const rootMeta = await resolveRootFolderId()
                    if (!rootMeta) {
                        if (requestToken === requestTokenRef.current) {
                            setItems([])
                            setFolderId(null)
                            setBreadcrumbs([{ id: null, name: 'root' }])
                        }
                        return
                    }

                    targetFolderId = rootMeta.id
                    if (requestToken === requestTokenRef.current && options?.resetBreadcrumbs !== false) {
                        setBreadcrumbs([{ id: rootMeta.id, name: 'root' }])
                    }
                }

                const mapped = await fetchFolderContents(targetFolderId)

                if (requestToken !== requestTokenRef.current) {
                    return
                }

                setItems(mapped)
                setFolderId(targetFolderId)
            } finally {
                if (requestToken === requestTokenRef.current) {
                    setLoading(false)
                }
            }
        },
        [fetchFolderContents, resolveRootFolderId]
    )

    useEffect(() => {
        setCatalogSearch('')
        loadFolder(null, { resetBreadcrumbs: true })
    }, [catalogTab, loadFolder])

    const openFolder = useCallback(
        (item: CatalogFileItem) => {
            if (!item.isDirectory) {
                return
            }

            setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }])
            loadFolder(item.id, { resetBreadcrumbs: false })
        },
        [loadFolder]
    )

    const goToBreadcrumb = useCallback(
        (index: number) => {
            if (index < 0 || index >= breadcrumbs.length - 1) {
                return
            }

            const next = breadcrumbs.slice(0, index + 1)
            const target = next[next.length - 1]
            setBreadcrumbs(next)
            loadFolder(target.id ?? rootFolderIdRef.current, { resetBreadcrumbs: false })
        },
        [breadcrumbs, loadFolder]
    )

    const goBack = useCallback(() => {
        if (breadcrumbs.length < 2) {
            return
        }

        goToBreadcrumb(breadcrumbs.length - 2)
    }, [breadcrumbs.length, goToBreadcrumb])

    const catalogPayloadFromItem = (item: CatalogFileItem): PlaceModeOptions | null => {
        const placeType = elementTypeFromFile(item.fileType)
        if (!placeType || !item.src) {
            return null
        }

        return {
            sourceId: item.id,
            src: item.src,
            thumbnail: item.thumbnail,
            name: item.name,
            naturalW: item.width,
            naturalH: item.height,
        }
    }

    const backgroundFromItem = (item: CatalogFileItem): CanvasBgMedia | null => {
        if (item.isDirectory || (item.fileType !== 'image' && item.fileType !== 'video') || !item.src) {
            return null
        }

        return {
            src: item.src,
            thumbnail: item.thumbnail,
            fileType: item.fileType as 'image' | 'video',
            sourceId: item.id,
            name: item.name,
        }
    }

    return {
        items: mediaItems,
        loading,
        breadcrumbs,
        folderId,
        rootFolderId,
        catalogSearch,
        setCatalogSearch,
        canGoBack: breadcrumbs.length > 1,
        openFolder,
        goToBreadcrumb,
        goBack,
        reloadCatalog: () => loadFolder(folderId ?? rootFolderId),
        catalogPayloadFromItem,
        backgroundFromItem,
        getThumbSrc: (item: CatalogFileItem) => helpersFiles.getSrc(helpersFiles.getFileItem(item)),
    }
}
